import { reverse } from "@reactivated";

import {
    IModularConfiguratorPromoCard,
    IModularConfiguratorUpsellInfoModal,
    ModularConfiguratorPromoCards,
    ModularConfiguratorUpsellInfoModals,
} from "../apps/configurator/models";
import { ConcreteBundles, IConcreteBundle } from "../models/catalogue";
import { IProductID, isoProductID } from "../models/nominals";
import { check } from "../models/utils";
import { fetchJson } from "../utils/ajax";
import { PromiseMutex } from "../utils/mutex";

/**
 * Return the UpsellInfoModal content for any bundles that are associated
 * with the given product ID.
 */
export const getUpsellInfoModalContent = async (
    triggeringProductID: IProductID,
): Promise<readonly IModularConfiguratorUpsellInfoModal[]> => {
    const unwrappedTriggeringProductID =
        isoProductID.unwrap(triggeringProductID);
    const mutex = new PromiseMutex<
        readonly IModularConfiguratorUpsellInfoModal[]
    >(`product-upsell-info-modals-get-${unwrappedTriggeringProductID}`);
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(
            reverse("product-upsellinfomodals", {
                pk: unwrappedTriggeringProductID,
            }),
        ).then((body) => {
            return check(ModularConfiguratorUpsellInfoModals.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

/**
 * Return the UpsellInfoModal content for any bundles that are associated
 * with the given product ID.
 */
export const getPromoCards = async (
    triggeringProductID: IProductID,
): Promise<readonly IModularConfiguratorPromoCard[]> => {
    const unwrappedTriggeringProductID =
        isoProductID.unwrap(triggeringProductID);
    const mutex = new PromiseMutex<readonly IModularConfiguratorPromoCard[]>(
        `product-promo-cards-get-${unwrappedTriggeringProductID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(
            reverse("product-promocards", {
                pk: unwrappedTriggeringProductID,
            }),
        ).then((body) => {
            return check(ModularConfiguratorPromoCards.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

/**
 * Return the concrete bundles triggered by the given product ID, with full
 * suggested product data. Out-of-stock upgrades keep their adder price, even
 * when their stock record is disabled.
 */
export const getConcreteBundles = async (
    triggeringProductID: IProductID,
): Promise<readonly IConcreteBundle[]> => {
    const unwrappedTriggeringProductID =
        isoProductID.unwrap(triggeringProductID);
    const mutex = new PromiseMutex<readonly IConcreteBundle[]>(
        `product-configurator-bundles-get-${unwrappedTriggeringProductID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(
            reverse("product-configuratorbundles", {
                pk: unwrappedTriggeringProductID,
            }),
        ).then((body) => {
            return check(ConcreteBundles.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};
